import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Alert, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import AppSafeArea from '@/components/layout/AppSafeArea';
import { useNavigation, useRouter } from 'expo-router';
import * as ImagePicker from 'expo-image-picker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  CreditCard,
  Trash2,
  Users,
  Shield,
  AlertTriangle,
  Trophy,
  Image as ImageIcon,
  LogOut,
  MessageCircle,
} from 'lucide-react-native';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Switch } from '@/components/ui/Switch';
import { AppSelect } from '@/components/ui/AppSelect';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/Avatar';
import AppHeader from '@/components/AppHeader';
import InviteModal from '@/components/InviteModal';
import AdminPicksManagement from '@/components/AdminPicksManagement';
import MarchMadnessLeagueView from '@/components/MarchMadnessLeagueView';
import PaymentManagement from '@/components/PaymentManagement';
import { useLeagueContext } from '@/contexts/LeagueContext';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { apiRequestJson } from '@/lib/queryClient';
import { trpc, useTRPCClient } from '@/lib/trpc';
import { primeLeagueCache } from '@/lib/leagueCache';
import { clientLogger } from '@/lib/datadog';
import { colors } from '@/lib/theme';
import { isWeb } from '@/lib/platform';
import type { LeagueSeasonSummary, LeagueSummary } from '@/types/league';
import { getLandingHref, getSeasonEditorHref } from '@/lib/routes';
import type { PickLockPolicyConfig, PickLockMode } from '@shared/seasons';

interface League extends LeagueSummary {
  ownerId: string;
  logoUrl: string | null;
  isActive: boolean;
  chatEnabled: boolean;
  inviteCode: string;
  invitePermission?: string;
}

type TabType = 'settings' | 'seasons' | 'members' | 'payments' | 'admin' | 'danger';

const formatCurrencyDisplay = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '$0.00';
  }
  return `$${(value / 100).toFixed(2)}`;
};

const getSeasonScoringSystem = (season?: LeagueSeasonSummary) => {
  const config = (season?.scoringConfig as { legacyScoringSystem?: string } | null) ?? null;
  return config?.legacyScoringSystem === 'totalWins' ? 'totalWins' : 'oddsBased';
};

const getSeasonPickLockPolicy = (season?: LeagueSeasonSummary): PickLockPolicyConfig => {
  const config = (season?.scoringConfig as { pickLockPolicy?: PickLockPolicyConfig } | null) ?? null;
  const policy = config?.pickLockPolicy;
  if (policy?.mode === 'roundStart') return { mode: 'roundStart' };
  if (policy?.mode === 'afterFirstNGames') {
    return { mode: 'afterFirstNGames', gamesCount: policy.gamesCount ?? 1 };
  }
  return { mode: 'gameStart' };
};

const getPickLockLabel = (policy: PickLockPolicyConfig) => {
  if (policy.mode === 'roundStart') return 'First game in week/round';
  if (policy.mode === 'afterFirstNGames') return `After first ${policy.gamesCount ?? 1} games`;
  return 'Each game start time';
};

const parseSeasonAdditionalInfo = (
  season?: LeagueSeasonSummary,
): Record<string, unknown> | null => {
  if (!season?.additionalInfo) {
    return null;
  }
  if (typeof season.additionalInfo === 'string') {
    try {
      const parsed = JSON.parse(season.additionalInfo);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch (error) {
      clientLogger.warn('Failed to parse season additionalInfo payload', { error });
      return null;
    }
  }
  if (typeof season.additionalInfo === 'object') {
    return season.additionalInfo as Record<string, unknown>;
  }
  return null;
};

const getSeasonTemplateId = (season?: LeagueSeasonSummary) => {
  const info = parseSeasonAdditionalInfo(season);
  const value = info && typeof info.fromTemplate === 'string' ? info.fromTemplate : null;
  return value;
};

export default function LeagueSettingsScreen() {
  const seasonLoadingStyle = { marginTop: 12 };
  const loadingContentStyle = { flex: 1, justifyContent: 'center' as const };
  const navigation = useNavigation();
  const router = useRouter();
  const leagueContext = useLeagueContext();
  const leagueId = leagueContext.id;
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();
  const { user } = useAuth();
  const currentUserId = user?.id ?? '';
  const { toast } = useToast();
  const cachedLeague =
    (queryClient.getQueryData(['/api/leagues', leagueId, 'active']) as League | undefined) ??
    (queryClient.getQueryData(['/api/leagues', leagueId]) as League | undefined) ??
    ((queryClient.getQueryData(trpc.leagues.list.queryKey()) as LeagueSummary[] | undefined)?.find(
      (candidate) => String(candidate.id) === leagueId,
    ) as League | undefined);
  const { data: leagueData } = useQuery(
    trpc.leagues.detail.queryOptions(
      { leagueId },
      { enabled: leagueId.length > 0, initialData: cachedLeague as any },
    ),
  );
  const { data: permissions } = useQuery(
    trpc.leagues.permissions.queryOptions(
      { leagueId },
      { enabled: leagueId.length > 0 },
    ),
  );

  const contextualLeague =
    String(leagueContext.league?.id ?? '') === leagueId
      ? (leagueContext.league as League)
      : undefined;
  const baseLeague = leagueData ?? cachedLeague ?? contextualLeague;
  const effectivePermissions = permissions ?? leagueContext.permissions;

  const [activeTab, setActiveTab] = useState<TabType>('settings');
  const [formData, setFormData] = useState({
    name: baseLeague?.name ?? '',
    description: baseLeague?.description || '',
    logoUrl: baseLeague?.logoUrl || '',
    isActive: Boolean(baseLeague?.isActive),
    chatEnabled: baseLeague?.chatEnabled ?? true,
    invitePermission: baseLeague?.invitePermission || 'managers',
    superBowlSquaresMaxPerUser: String(baseLeague?.superBowlSquaresMaxPerUser ?? 1),
  });
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [pickLockGamesCountBySeason, setPickLockGamesCountBySeason] = useState<Record<string, string>>({});
  const initialFormDataRef = useRef(formData);
  const skipUnsavedPromptRef = useRef(false);

  const isOwner = effectivePermissions?.isOwner || baseLeague?.ownerId === currentUserId;
  const canManageSettings = isOwner || effectivePermissions?.isManager;
  const canManageSeasons = isOwner || effectivePermissions?.isManager;
  const canManageMembers = isOwner || effectivePermissions?.isManager;
  const canManageLeagueAdmin = isOwner || effectivePermissions?.isManager;
  const canManagePayments = canManageLeagueAdmin && !leagueContext.isMarchMadness;
  const hasUnsavedChanges = useMemo(() => {
    const initial = initialFormDataRef.current;
    return (
      initial.name !== formData.name ||
      initial.description !== formData.description ||
      initial.logoUrl !== formData.logoUrl ||
      initial.isActive !== formData.isActive ||
      initial.chatEnabled !== formData.chatEnabled ||
      initial.invitePermission !== formData.invitePermission ||
      initial.superBowlSquaresMaxPerUser !== formData.superBowlSquaresMaxPerUser
    );
  }, [formData]);

  useEffect(() => {
    if (!baseLeague) {
      return;
    }
    const nextFormData = {
      name: baseLeague.name,
      description: baseLeague.description || '',
      logoUrl: baseLeague.logoUrl || '',
      isActive: Boolean(baseLeague.isActive),
      chatEnabled: baseLeague.chatEnabled ?? true,
      invitePermission: baseLeague.invitePermission || 'managers',
      superBowlSquaresMaxPerUser: String(baseLeague.superBowlSquaresMaxPerUser ?? 1),
    };
    setFormData(nextFormData);
    initialFormDataRef.current = nextFormData;
    skipUnsavedPromptRef.current = false;
  }, [baseLeague]);

  useEffect(() => {
    if ((activeTab === 'payments' && !canManagePayments) || (activeTab === 'admin' && !canManageLeagueAdmin)) {
      setActiveTab('settings');
    }
  }, [activeTab, canManageLeagueAdmin, canManagePayments]);

  const league =
    baseLeague ??
    ({
      id: leagueId,
      name: '',
      description: null,
      weeklyBuyIn: '0',
      logoUrl: null,
      memberCount: 0,
      resolvedSeasonId: 'active',
      currentSeason: {
        id: '',
        label: '',
        isActive: false,
      },
      inviteCode: '',
      invitePermission: 'managers',
      ownerId: currentUserId,
      isActive: false,
      chatEnabled: true,
      superBowlSquaresMaxPerUser: 1,
      scoringSystem: 'oddsBased',
    } satisfies League);

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (event) => {
      if (skipUnsavedPromptRef.current || !hasUnsavedChanges) {
        return;
      }

      event.preventDefault();

      const proceed = () => {
        skipUnsavedPromptRef.current = true;
        navigation.dispatch(event.data.action);
      };

      if (isWeb) {
        const confirmed =
          typeof window !== 'undefined' &&
          window.confirm('Discard unsaved changes?');
        if (confirmed) {
          proceed();
        }
        return;
      }

      Alert.alert('Discard changes?', 'You have unsaved changes. Do you want to leave?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Discard', style: 'destructive', onPress: proceed },
      ]);
    });

    return unsubscribe;
  }, [navigation, hasUnsavedChanges]);

  // Update league settings mutation
  const updateLeagueMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return trpcClient.leagues.update.mutate({ ...data, leagueId: String(league.id) });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id] });
      queryClient.invalidateQueries(trpc.leagues.detail.queryFilter({ leagueId: String(league.id) }));
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      queryClient.invalidateQueries(trpc.chat.unreadSummary.queryFilter());
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast.show({
        type: 'success',
        title: 'Settings Updated',
        description: 'League settings have been saved successfully.',
      });
      initialFormDataRef.current = formData;
      skipUnsavedPromptRef.current = true;
      navigation.goBack();
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Update Failed',
        description: error.message || 'Failed to update league settings.',
      });
    },
  });

  // Delete league mutation
  const deleteLeagueMutation = useMutation({
    mutationFn: async () => {
      return trpcClient.leagues.delete.mutate({ leagueId: String(league.id) });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast.show({
        type: 'success',
        title: 'League Deleted',
        description: 'The league has been permanently deleted.',
      });
      skipUnsavedPromptRef.current = true;
      router.replace(getLandingHref());
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Delete Failed',
        description: error.message || 'Failed to delete the league.',
      });
    },
  });

  const {
    data: seasonList,
    isLoading: seasonsLoading,
  } = useQuery<LeagueSeasonSummary[]>(
    trpc.seasons.listByLeague.queryOptions(
      { leagueId: String(league.id) },
      { enabled: Boolean(league?.id) },
    ) as any,
  );
  const seasons = seasonList ?? [];

  const { data: members = [], isLoading: membersLoading } = useQuery(
    trpc.leagues.members.queryOptions(
      { leagueId: String(league.id) },
      { enabled: Boolean(league?.id) },
    ),
  );

  const sortedMembers = useMemo(() => {
    const copy = [...members];
    return copy.sort((a, b) => {
      const aIsOwner = a.users.id === league.ownerId;
      const bIsOwner = b.users.id === league.ownerId;
      if (aIsOwner !== bIsOwner) return aIsOwner ? -1 : 1;
      const aIsManager = a.league_members.isManager;
      const bIsManager = b.league_members.isManager;
      if (aIsManager !== bIsManager) return aIsManager ? -1 : 1;
      const aName = `${a.users.firstName ?? ''} ${a.users.lastName ?? ''}`.trim() || a.users.email;
      const bName = `${b.users.firstName ?? ''} ${b.users.lastName ?? ''}`.trim() || b.users.email;
      return aName.localeCompare(bName);
    });
  }, [members, league.ownerId]);

  const toggleManagerMutation = useMutation({
    mutationFn: async ({ targetUserId, makeManager }: { targetUserId: string; makeManager: boolean }) => {
      const input = { leagueId: String(league.id), targetUserId };
      return makeManager
        ? trpcClient.leagues.promoteMember.mutate(input)
        : trpcClient.leagues.demoteMember.mutate(input);
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [`/api/leagues/${league.id}/members`] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id] });
      queryClient.invalidateQueries(trpc.leagues.members.queryFilter({ leagueId: String(league.id) }));
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      toast.show({
        type: 'success',
        title: variables.makeManager ? 'Manager Assigned' : 'Manager Removed',
        description: variables.makeManager
          ? 'This member is now a league manager.'
          : 'This member is no longer a league manager.',
      });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Update Failed',
        description: error.message || 'Failed to update manager status.',
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      return trpcClient.leagues.removeMember.mutate({
        leagueId: String(league.id),
        targetUserId,
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [`/api/leagues/${league.id}/members`] });
      queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id] });
      queryClient.invalidateQueries(trpc.leagues.members.queryFilter({ leagueId: String(league.id) }));
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast.show({
        type: 'success',
        title: 'Member Removed',
        description: 'The member has been removed from this league.',
      });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Removal Failed',
        description: error.message || 'Failed to remove member from league.',
      });
    },
  });

  const activateSeasonMutation = useMutation({
    mutationFn: async (seasonId: string) =>
      trpcClient.seasons.activate.mutate({ leagueId: String(league.id), seasonId }),
    onSuccess: async () => {
      toast.show({
        type: 'success',
        title: 'Season Activated',
        description: 'This season is now active for all members.',
      });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id] });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id, 'seasons'] });
      queryClient.invalidateQueries(trpc.seasons.listByLeague.queryFilter({ leagueId: String(league.id) }));
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    },
    onError: (error: any) => {
      toast.show({
        type: 'error',
        title: 'Activation Failed',
        description: error?.message ?? 'Unable to activate this season.',
      });
    },
  });

  const updateSeasonPickLockMutation = useMutation({
    mutationFn: async ({
      season,
      policy,
    }: {
      season: LeagueSeasonSummary;
      policy: PickLockPolicyConfig;
    }) => {
      const scoringConfig = {
        ...((season.scoringConfig as Record<string, unknown> | null) ?? {}),
        pickLockPolicy: policy,
      };
      return trpcClient.seasons.update.mutate({
        leagueId: String(league.id),
        seasonId: String(season.id),
        payload: { scoringConfig },
      });
    },
    onSuccess: async () => {
      toast.show({
        type: 'success',
        title: 'Pick Lock Updated',
        description: 'Season pick lock settings were saved.',
      });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id, 'seasons'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id] });
      await queryClient.invalidateQueries({ queryKey: ['/api/leagues', league.id, 'active'] });
      queryClient.invalidateQueries(trpc.seasons.listByLeague.queryFilter({ leagueId: String(league.id) }));
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
    },
    onError: () => {
      toast.show({
        type: 'error',
        title: 'Pick Lock Save Failed',
        description: 'Unable to update pick lock settings.',
      });
    },
  });

  interface UploadTargetResponse {
    uploadURL: string;
    objectPath?: string;
  }

  interface LeagueLogoResponse {
    objectPath: string;
  }

  const handleLogoUpload = async () => {
    try {
      setIsLogoUploading(true);
      // Request permissions
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        toast.show({
          type: 'error',
          title: 'Permission Denied',
          description: 'Camera roll permission is required to upload a logo.',
        });
        setIsLogoUploading(false);
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];

        // Step 1: Get upload URL from backend
        const { uploadURL, objectPath: providedObjectPath } = await apiRequestJson<UploadTargetResponse>(
          '/api/objects/upload',
          'POST',
        );

        if (!uploadURL) {
          throw new Error('Failed to get upload URL from server');
        }

        // Step 2: Upload image bytes directly to object storage
        const fileResponse = await fetch(asset.uri);
        const fileBlob = await fileResponse.blob();
        const uploadHeaders: HeadersInit = asset.mimeType ? { 'Content-Type': asset.mimeType } : {};

        const uploadResponse = await fetch(uploadURL, {
          method: 'PUT',
          body: fileBlob,
          headers: uploadHeaders,
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          throw new Error(`Failed to upload logo (${uploadResponse.status}): ${errorText}`);
        }

        // Step 3: Tell backend to make the logo public and return canonical path
        const uploadedPath = providedObjectPath || new URL(uploadURL).pathname;
        const { objectPath } = await apiRequestJson<LeagueLogoResponse>('/api/league-logos', 'PUT', {
          logoURL: uploadedPath,
        });

        // Step 4: Persist the new logo on the league so it survives refreshes
        const updatedLeague = await trpcClient.leagues.update.mutate({
          ...formData,
          logoUrl: objectPath,
          leagueId: String(league.id),
        });

        setFormData((prev) => {
          const nextFormData = { ...prev, logoUrl: updatedLeague.logoUrl || objectPath };
          initialFormDataRef.current = {
            ...initialFormDataRef.current,
            logoUrl: nextFormData.logoUrl,
          };
          return nextFormData;
        });
        primeLeagueCache(queryClient, updatedLeague as LeagueSummary);
        queryClient.invalidateQueries(trpc.leagues.detail.queryFilter({ leagueId: String(league.id) }));
        queryClient.invalidateQueries(trpc.leagues.list.queryFilter());

        toast.show({
          type: 'success',
          title: 'Logo Uploaded',
          description: 'Your league logo has been uploaded successfully.',
        });
      }
    } catch (error) {
      clientLogger.error('Logo upload error', { error });
      let description = 'Failed to upload the logo. Please try again.';
      if (error instanceof Error) {
        description = error.message;
        if (error.message.includes('Object storage is not available')) {
          description = 'League logo uploads are not available in local development. Please try again in the hosted environment.';
        }
      }
      toast.show({
        type: 'error',
        title: 'Upload Failed',
        description,
      });
    }
    finally {
      setIsLogoUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.show({
        type: 'error',
        title: 'Validation Error',
        description: 'League name is required.',
      });
      return;
    }

    const parsedSquaresLimit = Number(formData.superBowlSquaresMaxPerUser);
    if (!Number.isInteger(parsedSquaresLimit) || parsedSquaresLimit <= 0) {
      toast.show({
        type: 'error',
        title: 'Validation Error',
        description: 'Super Bowl squares per user must be a whole number greater than 0.',
      });
      return;
    }

    updateLeagueMutation.mutate(formData);
  };

  const handleDelete = () => {
    const title = 'Delete League';
    const message =
      `This action cannot be undone. This will permanently delete the league "${league.name}" and remove all associated picks, standings, and member data.`;

    if (isWeb) {
      const confirmed = typeof window !== 'undefined' ? window.confirm(`${title}\n\n${message}`) : false;
      if (confirmed) {
        deleteLeagueMutation.mutate();
      }
      return;
    }

    Alert.alert(title, message, [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text: 'Delete League',
        style: 'destructive',
        onPress: () => deleteLeagueMutation.mutate(),
      },
    ]);
  };

  const handleRemoveMember = (userId: string, displayName: string) => {
    const title = 'Remove member';
    const message = `Remove ${displayName} from this league? This cannot be undone.`;

    if (isWeb) {
      const confirmed = typeof window !== 'undefined' ? window.confirm(`${title}\n\n${message}`) : false;
      if (confirmed) {
        removeMemberMutation.mutate(userId);
      }
      return;
    }

    Alert.alert(title, message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: () => removeMemberMutation.mutate(userId),
      },
    ]);
  };

  const handleCreateSeasonPress = () => {
    router.push(getSeasonEditorHref(String(league.id), 'create'));
  };

  const handleSeasonEditPress = (season: LeagueSeasonSummary) => {
    router.push(getSeasonEditorHref(String(league.id), 'edit', String(season.id)));
  };

  const handlePickLockModeChange = (season: LeagueSeasonSummary, mode: PickLockMode) => {
    const currentPolicy = getSeasonPickLockPolicy(season);
    const seasonKey = String(season.id);
    const rawGamesCount =
      pickLockGamesCountBySeason[seasonKey] ?? String(currentPolicy.gamesCount ?? 1);
    const parsedGamesCount = Number.parseInt(rawGamesCount, 10);
    const policy: PickLockPolicyConfig =
      mode === 'afterFirstNGames'
        ? {
            mode,
            gamesCount: Number.isFinite(parsedGamesCount) && parsedGamesCount > 0 ? parsedGamesCount : 1,
          }
        : { mode };
    updateSeasonPickLockMutation.mutate({ season, policy });
  };

  const handlePickLockGamesCountSave = (season: LeagueSeasonSummary) => {
    const seasonKey = String(season.id);
    const rawGamesCount =
      pickLockGamesCountBySeason[seasonKey] ?? String(getSeasonPickLockPolicy(season).gamesCount ?? 1);
    const parsedGamesCount = Number.parseInt(rawGamesCount, 10);
    updateSeasonPickLockMutation.mutate({
      season,
      policy: {
        mode: 'afterFirstNGames',
        gamesCount: Number.isFinite(parsedGamesCount) && parsedGamesCount > 0 ? parsedGamesCount : 1,
      },
    });
  };

  const renderTabButton = (tab: TabType, label: string, icon: React.ReactNode) => (
    <TouchableOpacity
      style={[
        styles.tabButton,
        activeTab === tab && styles.tabButtonActive,
      ]}
      onPress={() => setActiveTab(tab)}
    >
      <View style={styles.tabIcon}>{icon}</View>
      <Text
        style={[
          styles.tabLabel,
          activeTab === tab && styles.tabLabelActive,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );

  const renderSettingsTab = () => (
    <ScrollView style={styles.flexOne} contentContainerStyle={styles.contentPadding}>
      {/* League Info */}
      <View style={styles.sectionIntro}>
        <View style={styles.inlineInfoRow}>
          <Shield size={16} color={colors.text.secondary} />
          <Text style={styles.subtleBodyText}>
            League Owner: {league.ownerId === currentUserId ? 'You' : 'Another member'}
          </Text>
        </View>
        <View style={styles.inlineInfoRow}>
          <Users size={16} color={colors.text.secondary} />
          <Text style={styles.subtleBodyText}>League Code: {league.inviteCode}</Text>
        </View>
      </View>

      {/* League Name */}
      <View style={styles.formSection}>
        <Label style={styles.foregroundLabel}>League Name</Label>
        <Input
          value={formData.name}
          onChangeText={(text) => setFormData({ ...formData, name: text })}
          editable={canManageSettings}
          placeholder="Enter league name"
        />
      </View>

      {/* Description */}
      <View style={styles.formSection}>
        <Label style={styles.foregroundLabel}>Description (Optional)</Label>
        <Textarea
          value={formData.description}
          onChangeText={(text) => setFormData({ ...formData, description: text })}
          editable={canManageSettings}
          placeholder="Add a description for your league..."
          numberOfLines={3}
        />
        <Text style={styles.helperText}>
          Tell members what makes your league special
        </Text>
      </View>

      {/* League Logo */}
      {canManageSettings && (
        <View style={styles.formSection}>
          <View style={styles.fieldHeaderRow}>
            <ImageIcon size={16} color={colors.text.secondary} />
            <Label style={styles.foregroundLabel}>League Logo</Label>
          </View>
          <View style={styles.logoRow}>
            <Avatar size={64} backgroundColor="paperDark" alignItems="center" justifyContent="center">
              {formData.logoUrl ? (
                <AvatarImage src={formData.logoUrl} />
              ) : (
                <AvatarFallback>
                  {league.name?.charAt(0)?.toUpperCase() || 'L'}
                </AvatarFallback>
              )}
            </Avatar>
            <Button variant="outline" onPress={handleLogoUpload} disabled={isLogoUploading}>
              <View style={styles.inlineInfoRow}>
                <ImageIcon size={16} color={colors.ink.press} />
                <Text style={styles.primaryActionText}>
                  {isLogoUploading ? 'Uploading...' : 'Upload Logo'}
                </Text>
              </View>
            </Button>
          </View>
          <Text style={styles.helperText}>
            Upload a custom logo for your league (max 5MB)
          </Text>
        </View>
      )}

      {/* Invite Permission */}
      <View style={styles.formSection}>
        <View style={styles.fieldHeaderRow}>
          <Users size={16} color={colors.text.secondary} />
          <Label style={styles.foregroundLabel}>Who Can Invite Members</Label>
        </View>
        <AppSelect
          value={formData.invitePermission}
          onChange={(value) => setFormData({ ...formData, invitePermission: value })}
          options={[
            { value: 'owner', label: 'Owner Only' },
            { value: 'managers', label: 'Owner & Managers' },
            { value: 'everyone', label: 'All Members' },
          ]}
          placeholder="Select invite permission"
          disabled={!canManageSettings}
        />
        <Text style={styles.helperText}>
          Control who can invite new members to your league
        </Text>
      </View>

      <View style={styles.formSection}>
        <Label style={styles.foregroundLabel}>Super Bowl Squares Per User</Label>
        <Input
          value={formData.superBowlSquaresMaxPerUser}
          onChangeText={(text) => {
            const digitsOnly = text.replace(/[^0-9]/g, '');
            setFormData({ ...formData, superBowlSquaresMaxPerUser: digitsOnly });
          }}
          editable={canManageSettings}
          placeholder="1"
          keyboardType="number-pad"
        />
        <Text style={styles.helperText}>
          Maximum squares each member can claim on the Super Bowl board.
        </Text>
      </View>

      {/* League Chat */}
      <View style={styles.activeLeagueCard}>
        <View style={styles.splitRow}>
          <View style={styles.toggleCopy}>
            <View style={styles.fieldHeaderRow}>
              <MessageCircle size={16} color={colors.text.secondary} />
              <Label style={styles.foregroundLabel}>League Chat</Label>
            </View>
            <Text style={styles.helperText}>
              Show or hide league chat for members. Disabling chat removes the chat tab.
            </Text>
          </View>
          <Switch
            checked={formData.chatEnabled}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, chatEnabled: checked }))
            }
            disabled={!canManageSettings}
          />
        </View>
      </View>

      {/* Active League */}
      <View style={styles.activeLeagueCard}>
        <View style={styles.splitRow}>
          <View style={styles.toggleCopy}>
            <Label style={styles.foregroundLabel}>Active League</Label>
            <Text style={styles.helperText}>
              Inactive leagues are hidden from the main switcher and automatic league notifications are paused. Open the Inactive leagues section to reactivate one.
            </Text>
          </View>
          <Switch
            checked={formData.isActive}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, isActive: checked }))
            }
            disabled={!canManageSettings}
          />
        </View>
      </View>

      {/* Action Buttons */}
      {canManageSettings && (
        <View style={styles.actionRow}>
          <Button variant="outline" onPress={() => navigation.goBack()} style={styles.flexOne}>
            <Text style={styles.secondaryButtonText}>Cancel</Text>
          </Button>
          <Button
            onPress={handleSubmit}
            disabled={updateLeagueMutation.isPending}
            style={styles.flexOne}
          >
            <Text style={styles.primaryButtonText}>
              {updateLeagueMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Text>
          </Button>
        </View>
      )}

      {!canManageSettings && (
        <Button variant="outline" onPress={() => navigation.goBack()} style={styles.marginTopLarge}>
          <Text>Close</Text>
        </Button>
      )}
    </ScrollView>
  );

  const renderSeasonManagementTab = () => (
    <ScrollView style={styles.flexOne} contentContainerStyle={styles.contentPadding}>
      {!canManageSeasons ? (
        <View style={styles.sectionIntro}>
          <Text style={styles.centeredEmptyTitle}>
            Season management unavailable
          </Text>
          <Text style={styles.centeredBodyText}>
            Only owners or managers can view this tab.
          </Text>
        </View>
      ) : seasonsLoading ? (
        <ActivityIndicator style={seasonLoadingStyle} />
      ) : (
        <>
          {seasons.map((seasonItem) => {
            const isActiveSeason = seasonItem.isActive;
            const editableTemplateId = getSeasonTemplateId(seasonItem);
            const isEditableSeason = true;
            const pickLockPolicy = getSeasonPickLockPolicy(seasonItem);
            const seasonKey = String(seasonItem.id);
            const pickLockGamesCount =
              pickLockGamesCountBySeason[seasonKey] ?? String(pickLockPolicy.gamesCount ?? 1);
            return (
              <View key={seasonItem.id} style={styles.seasonCard}>
                <View style={styles.seasonCardHeader}>
                  <View>
                    <Text style={styles.cardTitleText}>{seasonItem.label}</Text>
                    <Text style={styles.captionText}>
                      Template:{' '}
                      {editableTemplateId ? editableTemplateId : 'Legacy (editable)'}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.statusPill,
                      {
                        backgroundColor: isActiveSeason ? colors.success[100] : colors.neutral[200],
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillText,
                        {
                          color: isActiveSeason ? colors.success[800] : colors.text.secondary,
                        },
                      ]}
                    >
                      {isActiveSeason ? 'Active' : 'Inactive'}
                    </Text>
                  </View>
                </View>
                <View style={styles.compactStack}>
                  <Text style={styles.neutralBodyText}>
                    Weekly Buy-In: {formatCurrencyDisplay(seasonItem.weeklyBuyInCents)}
                  </Text>
                  <Text style={styles.neutralBodyText}>
                    Season Buy-In: {formatCurrencyDisplay(seasonItem.seasonBuyInCents)}
                  </Text>
                  <Text style={styles.neutralBodyText}>
                    Scoring:{' '}
                    {getSeasonScoringSystem(seasonItem) === 'totalWins' ? 'Total Wins' : 'Odds-Based'}
                  </Text>
                  <Text style={styles.neutralBodyText}>
                    Pick Lock: {getPickLockLabel(pickLockPolicy)}
                  </Text>
                </View>
                <View style={styles.pickLockControl}>
                  <Label>Pick Lock</Label>
                  <AppSelect
                    value={pickLockPolicy.mode}
                    onChange={(value) => handlePickLockModeChange(seasonItem, value as PickLockMode)}
                    options={[
                      { value: 'gameStart', label: 'At each game start time' },
                      { value: 'roundStart', label: 'At first game start time for week/round' },
                      { value: 'afterFirstNGames', label: 'After first N games' },
                    ]}
                    placeholder="Select pick lock"
                    disabled={updateSeasonPickLockMutation.isPending}
                  />
                  {pickLockPolicy.mode === 'afterFirstNGames' && (
                    <View style={styles.pickLockNRow}>
                      <Input
                        value={pickLockGamesCount}
                        onChangeText={(value) =>
                          setPickLockGamesCountBySeason((prev) => ({
                            ...prev,
                            [seasonKey]: value,
                          }))
                        }
                        placeholder="1"
                        keyboardType="number-pad"
                        style={styles.pickLockNInput}
                      />
                      <Button
                        variant="outline"
                        onPress={() => handlePickLockGamesCountSave(seasonItem)}
                        disabled={updateSeasonPickLockMutation.isPending}
                        style={styles.pickLockNSaveButton}
                      >
                        <Text>Save N</Text>
                      </Button>
                    </View>
                  )}
                </View>
                <View style={styles.actionRow}>
                  <Button
                    onPress={() => handleSeasonEditPress(seasonItem)}
                    style={styles.flexOne}
                  >
                    <Text style={styles.primaryButtonStrongText}>
                      {isEditableSeason ? 'Edit Full Season Settings' : 'Not Editable'}
                    </Text>
                  </Button>
                  {!isActiveSeason && canManageSeasons && (
                    <Button
                      onPress={() => activateSeasonMutation.mutate(String(seasonItem.id))}
                      disabled={activateSeasonMutation.isPending}
                      style={styles.flexOne}
                    >
                      <Text style={styles.primaryButtonStrongText}>
                        {activateSeasonMutation.isPending ? 'Activating...' : 'Set Active'}
                      </Text>
                    </Button>
                  )}
                </View>
              </View>
            );
          })}
          <View style={styles.formSection}>
            <View style={styles.fieldHeaderRow}>
              <Trophy size={16} color={colors.text.secondary} />
              <Label style={styles.foregroundLabel}>Create New Season</Label>
            </View>
            <Button
              onPress={handleCreateSeasonPress}
              style={styles.marginTopMedium}
            >
              <Text style={styles.primaryButtonStrongText}>Create Season</Text>
            </Button>
          </View>
        </>
      )}
    </ScrollView>
  );

  const renderMembersTab = () => (
    <ScrollView style={styles.flexOne} contentContainerStyle={styles.contentPaddingBottom}>
      <View style={styles.membersIntro}>
        <Text style={styles.sectionHeading}>League Members</Text>
        <Text style={styles.helperText}>
          {canManageMembers
            ? 'Promote managers or remove members to keep things organized.'
            : 'Only league owners and managers can manage members.'}
        </Text>
      </View>
      {membersLoading ? (
        <View style={styles.loadingState}>
          <ActivityIndicator size="small" color={colors.ink.press} />
          <Text style={styles.captionText}>Loading members...</Text>
        </View>
      ) : sortedMembers.length === 0 ? (
        <Text style={styles.emptyStateText}>
          No members found for this league.
        </Text>
      ) : (
        sortedMembers.map((member) => {
          const isOwnerMember = member.users.id === league.ownerId;
          const isManagerMember = member.league_members.isManager;
          const isSelf = member.users.id === currentUserId;
          const displayName =
            `${member.users.firstName ?? ''} ${member.users.lastName ?? ''}`.trim() ||
            member.users.email;
          const initials =
            `${member.users.firstName ?? ''} ${member.users.lastName ?? ''}`
              .trim()
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')
              .toUpperCase() || member.users.email?.[0]?.toUpperCase() || 'U';
          const isToggling =
            toggleManagerMutation.isPending &&
            toggleManagerMutation.variables?.targetUserId === member.users.id;
          const isRemoving =
            removeMemberMutation.isPending &&
            removeMemberMutation.variables === member.users.id;

          return (
            <View key={member.league_members.id} style={styles.memberCard}>
              <View style={styles.memberHeaderRow}>
                <Avatar size={44} backgroundColor="primary" alignItems="center" justifyContent="center">
                  <AvatarFallback backgroundColor="primary" textProps={{ color: 'white' }}>
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <View style={styles.flexOneCompact}>
                  <Text style={styles.memberNameText}>
                    {displayName}
                    {isSelf ? ' (You)' : ''}
                  </Text>
                  <Text style={styles.captionText}>{member.users.email}</Text>
                </View>
                <View style={styles.alignEndCompact}>
                  {isOwnerMember && (
                    <View style={styles.ownerPill}>
                      <Text style={styles.memberRoleText}>Owner</Text>
                    </View>
                  )}
                  {!isOwnerMember && isManagerMember && (
                    <View style={styles.managerPill}>
                      <Text style={styles.memberRoleText}>Manager</Text>
                    </View>
                  )}
                </View>
              </View>
              {canManageMembers && !isOwnerMember && !isSelf && (
                <View style={styles.memberActionRow}>
                  <Button
                    variant="outline"
                    onPress={() =>
                      toggleManagerMutation.mutate({
                        targetUserId: member.users.id,
                        makeManager: !isManagerMember,
                      })
                    }
                    disabled={isToggling || isRemoving}
                    style={styles.flexOne}
                  >
                    <View style={styles.buttonIconRow}>
                      <Shield size={14} color={colors.ink.press} />
                      <Text style={styles.primaryPillText}>
                        {isToggling
                          ? 'Updating...'
                          : isManagerMember
                            ? 'Remove Manager'
                            : 'Make Manager'}
                      </Text>
                    </View>
                  </Button>
                  <Button
                    variant="destructive"
                    onPress={() => handleRemoveMember(member.users.id, displayName)}
                    disabled={isRemoving || isToggling}
                    style={styles.flexOne}
                  >
                    <View style={styles.buttonIconRow}>
                      <Trash2 size={14} color={colors.text.inverse} />
                      <Text style={styles.inversePillText}>
                        {isRemoving ? 'Removing...' : 'Remove'}
                      </Text>
                    </View>
                  </Button>
                </View>
              )}
            </View>
          );
        })
      )}
    </ScrollView>
  );

  const renderPaymentsTab = () => (
    <ScrollView style={styles.flexOne} contentContainerStyle={styles.contentPaddingBottom}>
      <PaymentManagement
        leagueId={String(league.id)}
        seasonId={leagueContext.resolvedSeasonId ?? undefined}
      />
    </ScrollView>
  );

  const renderAdminTab = () => (
    leagueContext.isMarchMadness ? (
      <View style={styles.flexOne}>
        <MarchMadnessLeagueView
          leagueId={String(league.id)}
          seasonId={leagueContext.resolvedSeasonId || ''}
          section="admin"
          currentUserId={leagueContext.league?.currentUserId}
          permissions={leagueContext.permissions}
        />
      </View>
    ) : (
      <ScrollView style={styles.flexOne} contentContainerStyle={styles.contentPaddingBottom}>
        <AdminPicksManagement
          leagueId={String(league.id)}
          selectedWeekId={leagueContext.activeWeek?.id || ''}
          seasonId={leagueContext.resolvedSeasonId ?? undefined}
        />
      </ScrollView>
    )
  );

  const renderDangerTab = () => (
    <View style={styles.dangerTabContainer}>
      {isOwner ? (
        <View style={styles.dangerCard}>
          <View style={styles.dangerHeader}>
            <AlertTriangle size={20} color={colors.error[500]} />
            <Text style={styles.dangerTitle}>Danger Zone</Text>
          </View>
          <Text style={styles.dangerBodyText}>
            Once you delete a league, there is no going back. Please be certain.
          </Text>
          <Button
            onPress={handleDelete}
            disabled={deleteLeagueMutation.isPending}
            style={styles.deleteButton}
          >
            <View style={styles.deleteButtonContent}>
              <Trash2 size={16} color={colors.text.inverse} />
              <Text style={styles.primaryButtonText}>
                {deleteLeagueMutation.isPending ? 'Deleting...' : 'Delete League'}
              </Text>
            </View>
          </Button>
        </View>
      ) : (
        <View style={styles.dangerCard}>
          <Text style={styles.dangerBodyText}>
            You can leave this league at any time. You will lose access to all league data.
          </Text>
          <Button variant="outline" style={styles.leaveButton}>
            <View style={styles.deleteButtonContent}>
              <LogOut size={16} color={colors.error[500]} />
              <Text style={styles.leaveButtonText}>Leave League</Text>
            </View>
          </Button>
        </View>
      )}
    </View>
  );

  if (!baseLeague) {
    return (
      <AppSafeArea style={styles.safeArea} edges={['left', 'right', 'bottom']}>
        <AppHeader title="League Settings" currentLeagueId={leagueId} allowBack={false} />
        <View style={[styles.loadingState, loadingContentStyle]}>
          <ActivityIndicator size="large" color={colors.ink.press} />
        </View>
      </AppSafeArea>
    );
  }

  return (
    <AppSafeArea style={styles.safeArea} edges={['left', 'right', 'bottom']}>
      <AppHeader title="League Settings" currentLeagueId={String(league.id)} allowBack={false} />
      <View style={styles.screenBody}>
        {effectivePermissions?.canInvite && league.inviteCode ? (
          <View style={styles.topActionRow}>
            <InviteModal
              leagueId={String(league.id)}
              leagueName={league.name}
              inviteCode={league.inviteCode}
            />
          </View>
        ) : null}
        <Text style={styles.headerCopy}>
          {canManageSettings
            ? 'Manage league settings, seasons, and members based on your permissions.'
            : 'View league information. Only league owners and managers can modify settings.'}
        </Text>

        {/* Tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tabsScroller}
          contentContainerStyle={styles.tabsRow}
        >
          {renderTabButton(
            'settings',
            'Settings',
            <Settings size={16} color={activeTab === 'settings' ? colors.ink.field : colors.text.secondary} />
          )}
          {canManageSeasons &&
            renderTabButton(
              'seasons',
              'Seasons',
              <Trophy size={16} color={activeTab === 'seasons' ? colors.ink.field : colors.text.secondary} />,
            )}
          {renderTabButton(
            'members',
            'Members',
            <Users size={16} color={activeTab === 'members' ? colors.ink.field : colors.text.secondary} />
          )}
          {canManagePayments &&
            renderTabButton(
              'payments',
              'Payments',
              <CreditCard size={16} color={activeTab === 'payments' ? colors.ink.field : colors.text.secondary} />,
            )}
          {canManageLeagueAdmin &&
            renderTabButton(
              'admin',
              'Admin',
              <Shield size={16} color={activeTab === 'admin' ? colors.ink.field : colors.text.secondary} />,
            )}
          {renderTabButton(
            'danger',
            'Danger',
            <AlertTriangle size={16} color={activeTab === 'danger' ? colors.ink.field : colors.text.secondary} />
          )}
        </ScrollView>

        {/* Tab Content */}
        {activeTab === 'settings' && renderSettingsTab()}
        {activeTab === 'seasons' && renderSeasonManagementTab()}
        {activeTab === 'members' && renderMembersTab()}
        {activeTab === 'payments' && renderPaymentsTab()}
        {activeTab === 'admin' && renderAdminTab()}
        {activeTab === 'danger' && renderDangerTab()}
      </View>
    </AppSafeArea>
  );
}

const styles = StyleSheet.create(() => ({
  actionRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  activeLeagueCard: {
    backgroundColor: colors.card,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
    padding: 16,
  },
  alignEndCompact: {
    alignItems: 'flex-end',
    gap: 4,
  },
  buttonIconRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 6,
    justifyContent: 'center',
  },
  captionText: {
    color: colors.ink.pressMuted,
    fontSize: 12,
  },
  cardTitleText: {
    color: colors.text.primary,
    fontSize: 18,
    fontWeight: '600',
  },
  centeredBodyText: {
    color: colors.ink.pressMuted,
    fontSize: 14,
    textAlign: 'center',
  },
  centeredEmptyTitle: {
    color: colors.text.primary,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 40,
    textAlign: 'center',
  },
  compactStack: {
    gap: 4,
    marginBottom: 12,
  },
  contentPadding: {
    padding: 20,
  },
  contentPaddingBottom: {
    padding: 20,
    paddingBottom: 24,
  },
  dangerBodyText: {
    color: colors.error[900],
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  dangerCard: {
    backgroundColor: colors.error[50],
    borderColor: colors.state.errorBorder,
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
  },
  dangerHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  dangerTabContainer: {
    flex: 1,
    padding: 20,
  },
  dangerTitle: {
    color: colors.error[800],
    fontSize: 18,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: colors.error[500],
    borderColor: colors.error[500],
    borderWidth: 1,
  },
  deleteButtonContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
  },
  emptyStateText: {
    color: colors.text.secondary,
    fontSize: 14,
    paddingVertical: 24,
    textAlign: 'center',
  },
  fieldHeaderRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  flexOne: {
    flex: 1,
  },
  flexOneCompact: {
    flex: 1,
    gap: 2,
  },
  foregroundLabel: {
    color: colors.ink.press,
  },
  formSection: {
    marginBottom: 20,
  },
  headerCopy: {
    color: colors.ink.pressMuted,
    fontSize: 14,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  helperText: {
    color: colors.ink.pressMuted,
    fontSize: 12,
    marginTop: 4,
  },
  inlineInfoRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  inversePillText: {
    color: colors.ink.press,
    fontFamily: 'IBMPlexMono_600SemiBold',
    fontSize: 12,
    fontWeight: '600',
  },
  leaveButton: {
    borderColor: colors.error[500],
  },
  leaveButtonText: {
    color: colors.error[500],
    fontSize: 16,
    fontWeight: '500',
  },
  loadingState: {
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    paddingVertical: 24,
  },
  logoRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
  },
  managerPill: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.cardBorder,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  marginTopLarge: {
    marginTop: 24,
  },
  marginTopMedium: {
    marginTop: 12,
  },
  memberActionRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  memberCard: {
    backgroundColor: colors.card,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
    padding: 12,
  },
  memberHeaderRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
  },
  memberNameText: {
    color: colors.text.primary,
    fontSize: 15,
    fontWeight: '600',
  },
  memberRoleText: {
    color: colors.neutral[800],
    fontSize: 11,
    fontWeight: '600',
  },
  membersIntro: {
    marginBottom: 16,
  },
  neutralBodyText: {
    color: colors.ink.pressMuted,
    fontSize: 14,
  },
  ownerPill: {
    backgroundColor: colors.state.successBg,
    borderColor: colors.state.successBorder,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  pickLockControl: {
    gap: 8,
    marginBottom: 16,
  },
  pickLockNInput: {
    flex: 1,
  },
  pickLockNRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
  },
  pickLockNSaveButton: {
    minWidth: 96,
  },
  pillText: {
    fontSize: 12,
    fontWeight: '600',
  },
  primaryActionText: {
    color: colors.ink.press,
    fontFamily: 'IBMPlexMono_600SemiBold',
    fontSize: 14,
    textTransform: 'uppercase',
  },
  primaryButtonStrongText: {
    color: colors.text.inverse,
    fontSize: 16,
    fontWeight: '600',
  },
  primaryButtonText: {
    color: colors.text.inverse,
    fontSize: 16,
  },
  primaryPillText: {
    color: colors.ink.press,
    fontFamily: 'IBMPlexMono_600SemiBold',
    fontSize: 12,
    fontWeight: '600',
  },
  safeArea: {
    backgroundColor: colors.background,
  },
  screenBody: {
    backgroundColor: colors.background,
    flex: 1,
  },
  seasonCard: {
    backgroundColor: colors.card,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    padding: 16,
  },
  seasonCardHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  secondaryButtonText: {
    color: colors.neutral[700],
    fontSize: 16,
  },
  sectionHeading: {
    color: colors.text.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  sectionIntro: {
    gap: 8,
    marginBottom: 24,
  },
  splitRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-between',
  },
  statusPill: {
    borderColor: colors.cardBorder,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  subtleBodyText: {
    color: colors.ink.pressMuted,
    fontSize: 14,
  },
  tabButton: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderBottomColor: 'transparent',
    borderBottomWidth: 2,
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'center',
    minWidth: 104,
    paddingHorizontal: 8,
    paddingVertical: 12,
  },
  tabButtonActive: {
    borderBottomColor: colors.ink.field,
  },
  tabIcon: {
    marginRight: 6,
  },
  tabLabel: {
    color: colors.ink.pressMuted,
    fontFamily: 'IBMPlexMono_600SemiBold',
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  tabLabelActive: {
    color: colors.ink.field,
  },
  tabsRow: {
    flexDirection: 'row',
    flexGrow: 1,
  },
  tabsScroller: {
    borderBottomColor: colors.cardBorder,
    borderBottomWidth: 1,
    flexGrow: 0,
  },
  toggleCopy: {
    flex: 1,
  },
  topActionRow: {
    paddingHorizontal: 20,
    paddingTop: 16,
  },
}));
